import React, { useState } from 'react';
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  ProgressBar,
  Tabs,
  Table,
  Link,
  StatusIndicator,
  Button,
  ButtonDropdown,
  Modal,
  Alert,
  ExpandableSection
} from '@cloudscape-design/components';

// Dummy data for synthesis stages
const synthesisStagesData = [
  {
    id: 'entity-operations',
    name: 'Entity-Operations Synthesis',
    status: 'complete',
    progress: 100,
    domains: [
      { name: 'CustomerManagement', confidence: 92, changes: 3 },
      { name: 'OrderProcessing', confidence: 88, changes: 5 },
      // Add more domains
    ]
  },
  {
    id: 'entity-data',
    name: 'Entity-Data Synthesis',
    status: 'in-progress',
    progress: 60,
    domains: [
      { name: 'CustomerManagement', confidence: 90, changes: 2 },
      { name: 'OrderProcessing', confidence: 85, changes: 4 },
      // Add more domains
    ]
  },
  {
    id: 'final',
    name: 'Final Synthesis',
    status: 'pending',
    progress: 0,
    domains: []
  }
];

export function DomainSynthesisTab() {
  const [selectedStage, setSelectedStage] = useState(synthesisStagesData[0]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [showReconciliationModal, setShowReconciliationModal] = useState(false);

  const renderSynthesisProgress = () => (
    <Container
      header={
        <Header
          variant="h2"
          description="Progress of domain synthesis stages"
        >
          Synthesis Progress
        </Header>
      }
    >
      <SpaceBetween size="l">
        {synthesisStagesData.map((stage) => (
          <Box key={stage.id}>
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">{stage.name}</Box>
              <ProgressBar
                value={stage.progress}
                label={`${stage.progress}% complete`}
                description={
                  <StatusIndicator type={
                    stage.status === 'complete' ? 'success' :
                    stage.status === 'in-progress' ? 'in-progress' :
                    'pending'
                  }>
                    {stage.status}
                  </StatusIndicator>
                }
              />
            </SpaceBetween>
          </Box>
        ))}
      </SpaceBetween>
    </Container>
  );

  const renderDomainList = () => (
    <Container
      header={
        <Header
          variant="h2"
          description={`Domains after ${selectedStage.name}`}
          counter={`(${selectedStage.domains.length})`}
        >
          Synthesized Domains
        </Header>
      }
    >
      <Table
        columnDefinitions={[
          {
            id: 'name',
            header: 'Domain name',
            cell: item => (
              <Link onFollow={() => setSelectedDomain(item)}>
                {item.name}
              </Link>
            ),
            sortingField: 'name'
          },
          {
            id: 'confidence',
            header: 'Confidence',
            cell: item => `${item.confidence}%`,
            sortingField: 'confidence'
          },
          {
            id: 'changes',
            header: 'Changes',
            cell: item => item.changes,
            sortingField: 'changes'
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: item => (
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => setShowReconciliationModal(true)}>
                  View Reconciliation
                </Button>
                <Button>Compare Versions</Button>
              </SpaceBetween>
            )
          }
        ]}
        items={selectedStage.domains}
        sortingDisabled={false}
        variant="container"
        stickyHeader
        stripedRows
      />
    </Container>
  );

  const renderReconciliationModal = () => (
    <Modal
      visible={showReconciliationModal}
      onDismiss={() => setShowReconciliationModal(false)}
      header="Domain Reconciliation Details"
      size="large"
    >
      <SpaceBetween size="l">
        <Alert type="info">
          This view shows the reconciliation decisions made during the synthesis process.
        </Alert>
        <Table
          columnDefinitions={[
            {
              id: 'decision',
              header: 'Decision',
              cell: item => item.decision
            },
            {
              id: 'rationale',
              header: 'Rationale',
              cell: item => item.rationale
            },
            {
              id: 'impact',
              header: 'Impact',
              cell: item => (
                <StatusIndicator type={
                  item.impact === 'High' ? 'error' :
                  item.impact === 'Medium' ? 'warning' :
                  'success'
                }>
                  {item.impact}
                </StatusIndicator>
              )
            }
          ]}
          items={[
            { decision: 'Merge Customer and User entities', rationale: 'Overlapping responsibilities', impact: 'High' },
            { decision: 'Split OrderProcessing into Order and Payment', rationale: 'Separate concerns', impact: 'Medium' },
            // Add more reconciliation decisions
          ]}
          variant="embedded"
        />
      </SpaceBetween>
    </Modal>
  );

  return (
    <SpaceBetween size="l">
      {renderSynthesisProgress()}
      
      <Container
        header={
          <Header
            variant="h2"
            description="Compare and analyze domain synthesis stages"
            actions={
              <ButtonDropdown
                items={[
                  { text: 'Export synthesis report', id: 'export' },
                  { text: 'Rerun synthesis', id: 'rerun' },
                  { text: 'View full comparison', id: 'compare' }
                ]}
              >
                Actions
              </ButtonDropdown>
            }
          >
            Domain Synthesis Analysis
          </Header>
        }
      >
        <SpaceBetween size="l">
          <Tabs
            tabs={synthesisStagesData.map(stage => ({
              label: stage.name,
              id: stage.id,
              content: renderDomainList()
            }))}
            onChange={({ detail }) => {
              setSelectedStage(synthesisStagesData.find(stage => stage.id === detail.activeTabId));
            }}
          />
          
          <ExpandableSection header="Synthesis Insights">
            <ColumnLayout columns={2}>
              <Box>
                <SpaceBetween size="xs">
                  <Box variant="awsui-key-label">Total Domains</Box>
                  <Box variant="h3">{selectedStage.domains.length}</Box>
                </SpaceBetween>
              </Box>
              <Box>
                <SpaceBetween size="xs">
                  <Box variant="awsui-key-label">Average Confidence</Box>
                  <Box variant="h3">
                    {selectedStage.domains.length > 0
                      ? `${(selectedStage.domains.reduce((sum, domain) => sum + domain.confidence, 0) / selectedStage.domains.length).toFixed(1)}%`
                      : 'N/A'}
                  </Box>
                </SpaceBetween>
              </Box>
              <Box>
                <SpaceBetween size="xs">
                  <Box variant="awsui-key-label">Total Changes</Box>
                  <Box variant="h3">
                    {selectedStage.domains.reduce((sum, domain) => sum + domain.changes, 0)}
                  </Box>
                </SpaceBetween>
              </Box>
              <Box>
                <SpaceBetween size="xs">
                  <Box variant="awsui-key-label">Most Changed Domain</Box>
                  <Box variant="h3">
                    {selectedStage.domains.length > 0
                      ? selectedStage.domains.reduce((max, domain) => domain.changes > max.changes ? domain : max).name
                      : 'N/A'}
                  </Box>
                </SpaceBetween>
              </Box>
            </ColumnLayout>
          </ExpandableSection>
        </SpaceBetween>
      </Container>

      {renderReconciliationModal()}
    </SpaceBetween>
  );
};

